import { useState, useCallback, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL
const CANTIDAD_PARES = 8

function barajar(array) {
  const copia = [...array]
  for (let i = copia.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copia[i], copia[j]] = [copia[j], copia[i]]
  }
  return copia
}

/* Juego de Memoria: se muestra un tablero de cartas boca abajo,
   la mitad con palabras en inglés y la mitad con su traducción
   en español. El usuario voltea dos a la vez buscando parejas.
   Al encontrar todas, puede reclamar XP (con bono si tuvo pocos
   intentos fallidos). Se puede jugar tantas veces como se quiera. */
export default function MemoriaJuego({ usuario, onCambio }) {
  const [cargando,   setCargando]   = useState(true)
  const [cartas,     setCartas]     = useState([])
  const [pares,      setPares]      = useState([])
  const [volteadas,  setVolteadas]  = useState([])
  const [encontradas, setEncontradas] = useState([])
  const [intentos,   setIntentos]   = useState(0)
  const [bloqueado,  setBloqueado]  = useState(false)
  const [enviando,   setEnviando]   = useState(false)
  const [resultado,  setResultado]  = useState(null)
  const [error,      setError]      = useState(null)

  const nuevaPartida = useCallback(() => {
    setCargando(true)
    setError(null)
    setVolteadas([])
    setEncontradas([])
    setIntentos(0)
    setBloqueado(false)
    setResultado(null)
    axios.get(`${API}/juegos/memoria?cantidad=${CANTIDAD_PARES}`).then(res => {
      const datos = res.data.datos || []
      setPares(datos)
      const mazo = []
      datos.forEach(p => {
        mazo.push({ id: p.id + '-en', parId: p.id, tipo: 'ingles', texto: p.palabra_ingles })
        mazo.push({ id: p.id + '-es', parId: p.id, tipo: 'espanol', texto: p.palabra_espanol })
      })
      setCartas(barajar(mazo))
    }).catch(err => {
      console.error('Error cargando memoria:', err)
      setError('No pudimos cargar el tablero. Intenta de nuevo.')
    }).finally(() => setCargando(false))
  }, [])

  useEffect(() => { nuevaPartida() }, [nuevaPartida])

  function voltear(indice) {
    if (bloqueado) return
    if (volteadas.includes(indice)) return
    if (encontradas.includes(cartas[indice].parId)) return
    if (volteadas.length === 2) return

    const nuevasVolteadas = [...volteadas, indice]
    setVolteadas(nuevasVolteadas)

    if (nuevasVolteadas.length === 2) {
      setIntentos(n => n + 1)
      const [i1, i2] = nuevasVolteadas
      const c1 = cartas[i1], c2 = cartas[i2]
      if (c1.parId === c2.parId) {
        setTimeout(() => {
          setEncontradas(prev => [...prev, c1.parId])
          setVolteadas([])
        }, 500)
      } else {
        setBloqueado(true)
        setTimeout(() => {
          setVolteadas([])
          setBloqueado(false)
        }, 900)
      }
    }
  }

  async function reclamarXp() {
    if (enviando || !usuario?.id) return
    setEnviando(true)
    setError(null)
    try {
      const res = await axios.post(`${API}/juegos/memoria/completar`, {
        usuario_id: usuario.id, pares_totales: pares.length, intentos
      })
      setResultado(res.data)
      if (onCambio) onCambio()
    } catch (err) {
      console.error('Error guardando resultado de memoria:', err)
      setError('No pudimos guardar tu resultado. Intenta de nuevo.')
    } finally {
      setEnviando(false)
    }
  }

  if (cargando) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        Cargando tablero...
      </div>
    )
  }

  if (!pares.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🃏</div>
        <p>Todavía no hay palabras de memoria cargadas. ¡Vuelve pronto!</p>
      </div>
    )
  }

  const terminado = encontradas.length === pares.length

  return (
    <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 640 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>🃏 Memoria de Palabras</h1>
        <p style={{ color: 'var(--gris-texto)', fontSize: 14, marginTop: 4 }}>
          Encuentra los pares de palabras en inglés y español.
        </p>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>
          Parejas: {encontradas.length} / {pares.length}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>
          Intentos: {intentos}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 22 }}>
        {cartas.map((c, i) => {
          const encontrada = encontradas.includes(c.parId)
          const volteada = volteadas.includes(i) || encontrada
          const esErronea = volteadas.length === 2 && volteadas.includes(i) &&
            cartas[volteadas[0]].parId !== cartas[volteadas[1]].parId

          let fondo = '#fff', borde = 'var(--gris-borde)', color = '#1A1A2E'
          if (encontrada) { fondo = '#DCFCE7'; borde = '#22C55E' }
          else if (esErronea) { fondo = '#FEE2E2'; borde = '#EF4444' }
          else if (volteada) { fondo = c.tipo === 'ingles' ? '#EFF6FF' : '#FFF7ED'; borde = c.tipo === 'ingles' ? '#3B82F6' : '#F59E0B' }
          else { fondo = 'var(--azul-oscuro)'; color = '#fff' }

          return (
            <button key={c.id} onClick={() => voltear(i)} disabled={volteada || bloqueado}
              style={{
                aspectRatio: '1', borderRadius: 12, border: '1.5px solid ' + borde,
                background: fondo, color, fontFamily: 'inherit', fontWeight: 700,
                fontSize: volteada ? 11.5 : 20, padding: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                cursor: (volteada || bloqueado) ? 'default' : 'pointer', lineHeight: 1.2,
                transition: 'all .15s ease'
              }}>
              {volteada ? c.texto : '❓'}
            </button>
          )
        })}
      </div>

      {terminado && (
        <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 26, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>🎉</div>
          <h2 style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>¡Tablero completado!</h2>
          <p style={{ fontSize: 14, color: 'var(--gris-texto)', marginBottom: 20 }}>
            Encontraste las {pares.length} parejas en {intentos} intentos.
          </p>

          {resultado ? (
            <div>
              <div style={{ background: 'linear-gradient(135deg,#065F46,#059669)', color: '#fff', borderRadius: 14, padding: 18, marginBottom: 18 }}>
                <div style={{ fontSize: 20, fontWeight: 800 }}>+{resultado.xp_ganado} XP ganados</div>
              </div>
              <button onClick={nuevaPartida}
                style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'var(--azul)', color: '#fff',
                  fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                🔁 Jugar de nuevo
              </button>
            </div>
          ) : (
            <button onClick={reclamarXp} disabled={enviando}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', fontFamily: 'inherit',
                fontSize: 15, fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer',
                background: enviando ? '#CBD5E1' : 'var(--dorado)', color: '#fff' }}>
              {enviando ? 'Guardando...' : 'Reclamar XP'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
